import React, { Component } from 'react';
import PropTypes from 'prop-types';
import 'url-search-params-polyfill';
// jl-design-system
import { scrollTo } from 'jl-design-system/utils/scroll/scrollTo';
import env from 'jl-design-system/utils/env/env';
// only for use in local/dev/test environments
import FormAutoFill from 'jl-design-system/components/form/_dev/FormAutoFill';
// lodash
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
// classnames
import classNames from 'classnames';
// redux
import { connect } from 'react-redux';
import { handbackTo } from './redux/actions/data-action-link/dataActionLinkActions';
import { restoreAuth0Client } from './redux/actions/auth0-callback/auth0CallbackActions';
import { rebatchOrderAfterBasketEdit } from './redux/actions/edit-basket/editBasketActions';
import { initMonetateScript, appendGTMScript } from './redux/actions/app/appActions';
// constants
import appConstants from './constants/appConstants';
import routeConstants from './constants/routeConstants';
import featureConstants from './constants/featureConstants';
import { isFeatureActive } from './redux/reducers/config/configReducer';
// utils
import { getPathChunk } from './utils';
import { isApps, isPactApp } from './redux/reducers/app/appReducer';
import { loadIovationScripts } from './redux/actions/payment/paymentActions';
import { loadGooglePayScript } from './redux/actions/payment/googlePayActions';
import { initLogging } from './utils/logging/logging-utils';
import { isDeliveryPage, isPaymentPage } from './utils/helpers/pageType';
import shouldShowFullScreenSignUp from './utils/myjl/fullScreenSignUp';
// components
import Sidebar from './components/sidebar/Sidebar';
import Header from './components/header/Header';
import LoadingScreen from './components/loading-screen/LoadingScreen';
import GenericError from './views/generic-error/GenericError';
import SessionExpired from './views/session-expired/SessionExpired';
import OrderCompleteNotification from './components/order-complete-notification/OrderCompleteNotification';
import EmptyBasketNotification from './components/empty-basket-notification/EmptyBasketNotification';
import OrderTimeoutNotification from './components/order-timeout-notification/OrderTimeoutNotification';
import PactOrderFailed from './components/pact-order-failed/PactOrderFailed';
import PosCreditSessionExpiredError from './components/pos-credit-session-expired-error/PosCreditSessionExpiredError';
import AgeCheckModal from './components/age-check-modal/AgeCheckModal';
import SessionTimeoutWarning from './components/session-timeout-warning/SessionTimeoutWarning';
import SessionExpiryCounter from './components/session-expiry-counter/SessionExpiryCounter';
import SessionTimeoutRedirect from './components/session-timeout-redirect/SessionTimeoutRedirect';
import ConnectivityErrorModal from './components/connectivity-error-modal/ConnectivityErrorModal';
import LimitedStockModal from './components/limited-stock-modal/LimitedStockModal';
import BatchingFailureModal from './components/batching-failure-modal/BatchingFailureModal';
import EditBasketModal from './components/edit-basket-modal/EditBasketModal';
import DevConsole from './non-prod-utils/components/dev-console/DevConsole';
import Recommendations from './components/recommendations/Recommendations';
import UnavailableItemsModal from './components/unavailable-items-modal/UnavailableItemsModal';
import LeaveCheckoutModal from './components/leave-checkout-modal/LeaveCheckoutModal';

// styles
import styles from './app.scss';

export class App extends Component {

  static propTypes = {
    appendGTMScript: PropTypes.func,
    basketUrl: PropTypes.string,
    browseUrl: PropTypes.string,
    canonicalURL: PropTypes.string,
    children: PropTypes.node,
    editBasketModalBaseRoutePath: PropTypes.string,
    globalError: PropTypes.shape({
      body: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
        PropTypes.node,
      ]),
      code: PropTypes.string,
      message: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
      ]),
    }),
    handbackTo: PropTypes.func,
    initLogging: PropTypes.func,
    initMonetateScript: PropTypes.func,
    isExpressCheckout: PropTypes.bool,
    isGuest: PropTypes.bool,
    isInvalidPartnerDiscount: PropTypes.bool,
    isLoading: PropTypes.bool,
    leaveCheckoutModalBaseRoutePath: PropTypes.string,
    loadGooglePayScript: PropTypes.func,
    loadingMessage: PropTypes.string,
    loadIovationScripts: PropTypes.func,
    location: PropTypes.objectOf(PropTypes.oneOfType([
      PropTypes.shape({}),
      PropTypes.string,
    ])),
    orderComplete: PropTypes.bool,
    orderNeedsToBeRebatched: PropTypes.bool,
    pactOrderFailed: PropTypes.bool,
    pageNotFound: PropTypes.bool,
    pathname: PropTypes.string,
    posCreditSessionExpired: PropTypes.bool,
    rebatchOrderAfterBasketEdit: PropTypes.func,
    restoreAuth0Client: PropTypes.func,
    sessionExpired: PropTypes.bool,
    showConnectivityErrorModal: PropTypes.bool,
    showDisableSiteSpinner: PropTypes.bool,
    showEmptyBasketNotification: PropTypes.bool,
    showFullScreenSignup: PropTypes.bool,
    showHeader: PropTypes.bool,
    showRecommendations: PropTypes.bool,
    submitOrderTimeout: PropTypes.bool,
  };

  static defaultProps = {
    appendGTMScript: window.defaultFunc,
    basketUrl: '',
    browseUrl: '',
    canonicalURL: '',
    children: null,
    editBasketModalBaseRoutePath: '',
    globalError: undefined,
    handbackTo: window.defaultFunc,
    initLogging: window.defaultFunc,
    initMonetateScript: window.defaultFunc,
    isExpressCheckout: false,
    isGuest: true,
    isInvalidPartnerDiscount: false,
    isLoading: false,
    leaveCheckoutModalBaseRoutePath: '',
    loadGooglePayScript: window.defaultFunc,
    loadingMessage: '',
    loadIovationScripts: window.defaultFunc,
    location: {},
    orderComplete: false,
    orderNeedsToBeRebatched: false,
    pactOrderFailed: false,
    pageNotFound: false,
    pathname: '',
    posCreditSessionExpired: false,
    rebatchOrderAfterBasketEdit: window.defaultFunc,
    restoreAuth0Client: window.defaultFunc,
    sessionExpired: false,
    showConnectivityErrorModal: false,
    showDisableSiteSpinner: false,
    showEmptyBasketNotification: false,
    showFullScreenSignup: false,
    showHeader: true,
    showRecommendations: false,
    submitOrderTimeout: false,
  };

  constructor() {
    super();

    this.state = {
      sidebarVisibleResolution: false,
    };
  }

  componentDidMount() {
    if (window?.history.scrollRestoration === 'auto') {
      window.history.scrollRestoration = 'manual';
    }

    const {
      appendGTMScript,
      initLogging,
      restoreAuth0Client,
      loadGooglePayScript,
      loadIovationScripts,
      initMonetateScript,
    } = this.props;

    initLogging();

    this.windowMatchMedia = window.matchMedia(`(min-width: ${appConstants.SIDEBAR_MIN_BREAKPOINT})`);
    this.setSidebarVisibleResolution(this.windowMatchMedia.matches);
    this.windowMatchMedia.addListener(this.mediaQueryCheck);

    restoreAuth0Client();

    loadIovationScripts();
    loadGooglePayScript();
    initMonetateScript();
    appendGTMScript();
  }

  setSidebarVisibleResolution = (bool) => {
    this.setState({ sidebarVisibleResolution: bool });
  };

  mediaQueryCheck = () => {
    this.setSidebarVisibleResolution(this.windowMatchMedia.matches);
  };

  componentWillUnmount() {
    if (this.windowMatchMedia) {
      this.windowMatchMedia.removeListener(this.mediaQueryCheck);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      appendGTMScript,
      location,
      pathname,
      orderNeedsToBeRebatched,
      rebatchOrderAfterBasketEdit,
      restoreAuth0Client,
      loadGooglePayScript,
      loadIovationScripts,
      initMonetateScript,
      isGuest,
    } = this.props;

    if (
      includes(prevProps.pathname, routeConstants.EDIT_BASKET) &&
      !includes(pathname, routeConstants.EDIT_BASKET) &&
      orderNeedsToBeRebatched
    ) {
      rebatchOrderAfterBasketEdit();
    }

    const prevPath = prevProps.location?.pathname;
    const currPath = location?.pathname;
    const prevPathChunk = getPathChunk({ url: prevPath, chunk: 0 });
    const currentPathChunk = getPathChunk({ url: currPath, chunk: 0 });

    // only scroll to top if main path changes
    if (prevPathChunk !== currentPathChunk) {
      // console.warn('>> Route change: SCROLLING TO TOP');
      setTimeout(() => {
        scrollTo(0);
      });
    }

    if (prevProps.isGuest && !isGuest) {
      restoreAuth0Client();
    }

    loadIovationScripts();
    loadGooglePayScript();
    initMonetateScript();
    appendGTMScript();
  }

  render() {
    const {
      basketUrl,
      browseUrl,
      canonicalURL,
      children,
      editBasketModalBaseRoutePath,
      globalError,
      handbackTo,
      isExpressCheckout,
      isGuest,
      isInvalidPartnerDiscount,
      isLoading,
      leaveCheckoutModalBaseRoutePath,
      loadingMessage,
      orderComplete,
      pactOrderFailed,
      pathname,
      posCreditSessionExpired,
      sessionExpired,
      showConnectivityErrorModal,
      showDisableSiteSpinner,
      showEmptyBasketNotification,
      showHeader,
      showRecommendations,
      submitOrderTimeout,
      showFullScreenSignup,
      pageNotFound,
    } = this.props;

    const { sidebarVisibleResolution } = this.state;

    let mainContent = children;

    const showOrderCompleteNotification =
      orderComplete &&
      // TODO refactor this
      !includes(pathname, routeConstants.ORDER_CONFIRMATION) &&
      !includes(pathname, routeConstants.CALLBACK_ORDER_CLAIM) &&
      !includes(pathname, routeConstants.CALLBACK_CREATE_ACCOUNT) &&
      !includes(pathname, routeConstants.JOIN_MY_JL);

    const shouldShowHeader = showHeader && !includes([routeConstants.APPS_GUEST_HANDOVER,
      routeConstants.APPS_AUTHENTICATED_HANDOVER, routeConstants.PACT_HANDOVER], pathname);
    if (globalError) {
      mainContent = <GenericError />;
    } else {

      if (sessionExpired) {
        mainContent = (
          <SessionExpired canonicalURL={canonicalURL} showBackToBasket={showHeader} showBackToPDP={isExpressCheckout} />
        );
      }

      if (showOrderCompleteNotification) {
        mainContent = (
          <OrderCompleteNotification browseUrl={browseUrl} />
        );
      }

      if (showEmptyBasketNotification) {
        mainContent = (
          <EmptyBasketNotification handleButtonClick={() => handbackTo(browseUrl)} />
        );
      }

      if (submitOrderTimeout) {
        mainContent = (
          <OrderTimeoutNotification isGuest={isGuest} />
        );
      }

      if (posCreditSessionExpired) {
        mainContent = (
          <PosCreditSessionExpiredError
            handleButtonClick={() => handbackTo(basketUrl)}
            isInvalidPartnerDiscount={isInvalidPartnerDiscount}
          />
        );
      }

      if (pactOrderFailed) {
        mainContent = (
          <PactOrderFailed />
        );
      }
    }

    const deliveryPageActive = isDeliveryPage(pathname, editBasketModalBaseRoutePath, leaveCheckoutModalBaseRoutePath);
    const paymentPageActive = isPaymentPage(pathname, editBasketModalBaseRoutePath, leaveCheckoutModalBaseRoutePath);

    const showSidebar = !sessionExpired && !orderComplete && !posCreditSessionExpired &&
      isEmpty(globalError) && !submitOrderTimeout && sidebarVisibleResolution &&
      (deliveryPageActive || paymentPageActive);

    const centerVertical = !!globalError ||
      showEmptyBasketNotification ||
      showOrderCompleteNotification ||
      submitOrderTimeout ||
      sessionExpired ||
      pactOrderFailed ||
      pageNotFound;

    const containerClassNames = classNames(styles.container, {
      [styles.centerVertical]: centerVertical,
      [styles.sidebarVisible]: showSidebar,
    });

    return (
      <div className={styles.app}>

        <SessionExpiryCounter />

        {
          /* NOTE: This will not render in production */
          (env.isClientLocal || env.isClientDev) &&
          <FormAutoFill id="urlQueryParams" />
        }

        {
          shouldShowHeader &&
          <Header />
        }
        <main
          className={classNames(styles.main, {
            [styles.sidebarVisible]: showSidebar,
          })}
          data-test="main"
          id="main"
        >
          {
            !centerVertical &&
            (deliveryPageActive || paymentPageActive || includes(pathname, routeConstants.ORDER_CONFIRMATION)) &&
            <h1
              className={classNames(styles.mainHeading, {
                [styles.sidebarVisible]: showSidebar,
              })}
            >
              { orderComplete ? 'Order confirmation' : 'Checkout' }
            </h1>
          }

          <LoadingScreen
            isApps={!showHeader}
            message={loadingMessage}
            seeThrough={showDisableSiteSpinner}
            show={isLoading}
          />

          <section
            className={styles.section}
          >
            <div className={containerClassNames} data-test="main-content">
              {mainContent}
            </div>
            {
              showSidebar &&
              <Sidebar isDeliveryPage={deliveryPageActive} />
            }
          </section>
          {
            !sessionExpired &&
            <>
              <AgeCheckModal />
              <BatchingFailureModal />
              <LeaveCheckoutModal />
              <LimitedStockModal />
              <EditBasketModal />
              <UnavailableItemsModal />
            </>
          }

          {
            !sessionExpired &&
            !orderComplete &&
            !submitOrderTimeout &&
            !pactOrderFailed &&
            <SessionTimeoutWarning />
          }

          {
            !orderComplete &&
            <SessionTimeoutRedirect />
          }

          {
            showConnectivityErrorModal &&
            <ConnectivityErrorModal />
          }

          {
            /* NOTE: This will not render in production */
            env.isClientNonProd &&
            <DevConsole showHeader={showHeader} />
          }

        </main>

        {
          showRecommendations &&
          !showFullScreenSignup &&
          pathname === routeConstants.ORDER_CONFIRMATION &&
          <Recommendations />
        }
      </div>
    );
  }
}

export function mapStateToProps(state) {
  return {
    globalError: state.error.error,
    basketUrl: state.bff.basketUrl,
    browseUrl: state.bff.browseUrl,
    isLoading: state.app.isLoading,
    orderComplete: state.app.orderComplete,
    submitOrderTimeout: state.app.submitOrderTimeout,
    isGuest: state.user.isGuest,
    loadingMessage: state.app.loadingMessage,
    showHeader: !isApps(state),
    sessionExpired: state.app.sessionExpired,
    showDisableSiteSpinner: state.app.showDisableSiteSpinner,
    pathname: state?.router.location.pathname,
    orderNeedsToBeRebatched: state.orderForm?.orderNeedsToBeRebatched,
    showEmptyBasketNotification: state.app.showEmptyBasketNotification,
    showConnectivityErrorModal: state.app.showConnectivityErrorModal,
    pactOrderFailed: state.payment.pactOrderFailed,
    posCreditSessionExpired: state.app.posCreditSessionExpired,
    editBasketModalBaseRoutePath: state.delivery.editBasketModalBaseRoutePath,
    leaveCheckoutModalBaseRoutePath: state.app.leaveCheckoutModalBaseRoutePath,
    showRecommendations: !isApps(state) &&
      !isPactApp(state) &&
      isFeatureActive(state, featureConstants.SHOW_RECOMMENDATIONS) &&
      env.isClientProd,
    isInvalidPartnerDiscount: state.app.isInvalidPartnerDiscount,
    showFullScreenSignup: shouldShowFullScreenSignUp(state),
    pageNotFound: state.app.pageNotFound,
    canonicalURL: state.orderForm?.items[0]?.canonicalURL,
    isExpressCheckout: state.app.isExpressCheckout,
  };
}

export function mapDispatchToProps() {
  return {
    appendGTMScript,
    initLogging,
    rebatchOrderAfterBasketEdit,
    handbackTo,
    restoreAuth0Client,
    loadGooglePayScript,
    loadIovationScripts,
    initMonetateScript,
  };
}

export default connect(state => mapStateToProps(state), mapDispatchToProps())(App);
